import { num, type ComponentForm } from '../component-types.js';

/**
 * `pixelPerfectCamera` (Unity's Pixel Perfect Camera): goes next to an
 * orthographic `camera`. Whole-number zoom to the reference resolution,
 * camera / sprite snapping to the art pixel grid and optional crop bars
 * (see engine/docs/systems/2d.md).
 */
const form: ComponentForm = {
  key: 'pixelPerfectCamera',
  label: 'Pixel Perfect Camera',
  icon: 'camera',
  order: 31,
  defaults: { referenceResolution: [320, 180], pixelSnapping: true },
  fields: [
    {
      key: 'assetsPPU',
      label: 'Assets Pixels Per Unit',
      type: num(1, { min: 1 }),
      hint: 'Pixels per world unit of the art. Empty = settings.render.pixelsPerUnit.',
    },
    {
      key: 'referenceResolution',
      label: 'Reference Resolution',
      type: { kind: 'json' },
      hint: 'Design resolution in art pixels, [width, height], e.g. [320, 180]. The view zooms by the largest whole number that fits.',
      read: (v) => v.referenceResolution ?? [320, 180],
    },
    {
      key: 'pixelSnapping',
      label: 'Pixel Snapping',
      type: { kind: 'bool' },
      hint: 'Snap sprite and tilemap positions to the art pixel grid.',
      read: (v) => v.pixelSnapping ?? false,
    },
    {
      key: 'upscaleRenderTexture',
      label: 'Upscale Render Texture',
      type: { kind: 'bool' },
      hint: 'Move the camera and every sprite in whole art pixels (low-resolution look).',
      read: (v) => v.upscaleRenderTexture ?? false,
    },
    {
      key: 'cropFrame',
      label: 'Crop Frame',
      type: { kind: 'enum', options: ['none', 'letterbox', 'pillarbox', 'windowbox'] },
      hint: 'Black bars so exactly the reference resolution is visible: letterbox (top/bottom), pillarbox (sides), windowbox (both).',
      read: (v) => v.cropFrame ?? 'none',
    },
    {
      key: 'stretchFill',
      label: 'Stretch Fill',
      type: { kind: 'bool' },
      hint: 'Windowbox only: scale (not by whole numbers) to fill the screen, keeping the aspect ratio.',
      showIf: (v) => v.cropFrame === 'windowbox',
      read: (v) => v.stretchFill ?? false,
    },
  ],
};
export default form;
